import { Button } from "shared/components/Button";
import {
  COOKIE_UPDATE,
  COOKIE_VIDEO_MESSAGE,
  translate,
} from "shared/utils/translate";
import { FigureCaption } from "shared/components/FigureCaption";
import { MaxWidth } from "shared/components/MaxWidth";
import { PAGE_TYPE, VIMEO, YOUTUBE, MUX, MEDIUM } from "shared/utils/constants";
import { useAppContext } from "shared/utils/useAppContext";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import styles from "./Video.module.css";

import videojs from "@mux/videojs-kit/dist/index.vhs.js";
import "@mux/videojs-kit/dist/index.css";

export const VideoContainer = ({
  caption,
  credits,
  size = MEDIUM,
  type,
  videoId,
  thumbTime,
}) => {
  const url = getVideoEmbedUrl({ id: videoId, type });

  return (
    <MaxWidth size={size}>
      {(type === YOUTUBE || type === VIMEO) && (
        <Video caption={caption} credits={credits} url={url} />
      )}
      {type === MUX && <MuxVideo playbackId={videoId} thumbTime={thumbTime} />}
    </MaxWidth>
  );
};

export const MuxVideo = ({ playbackId, thumbTime }) => {
  const [videoEl, setVideoEl] = useState(null);
  const onVideo = useCallback((el) => {
    setVideoEl(el);
  }, []);

  useEffect(() => {
    if (videoEl == null) return;
    const player = videojs(videoEl, playbackId);

    // add download button to videojs controlbar
    const controlBar = player.getChild("ControlBar");
    const downloadButton = controlBar.addChild("button", {
      clickHandler: function (event) {
        window.open(
          `https://stream.mux.com/${playbackId}/medium.mp4?download=${playbackId}`,
          "Download"
        );
      },
    });

    downloadButton.controlText("Download");
    downloadButton.addClass("vjs-download");

    // move download button before fullscreen toggle
    controlBar
      .el()
      .insertBefore(
        downloadButton.el(),
        controlBar.getChild("fullscreenToggle").el()
      );

    return () => {
      player.dispose();
    };
  }, [playbackId, videoEl]);

  return (
    <video
      className="video-js vjs-16-9"
      controls
      preload="auto"
      width="100%"
      ref={onVideo}
      data-setup='{"timelineHoverPreviews": true}'
      poster={`https://image.mux.com/${playbackId}/thumbnail.jpg?${
        thumbTime ? "time=" + thumbTime : ""
      }&fit_mode=smartcrop`}
      key={playbackId}
    >
      <source src={playbackId} type="video/mux" />
    </video>
  );
};

export const Video = ({ caption, credits, url, id, service }) => {
  const { cookies } = useAppContext();

  if (!url && id && service) {
    url = getVideoEmbedUrl({ id, type: service });
  }

  return (
    <FigureCaption caption={caption} credits={credits}>
      <div className={styles.iframeContainer}>
        {cookies.advertising ? (
          <iframe frameBorder="0" src={url} allow="autoplay; fullscreen" />
        ) : (
          <VideoDisabled />
        )}
      </div>
    </FigureCaption>
  );
};

const VideoDisabled = () => {
  const { locale } = useRouter();

  return (
    <div className={styles.videoDisabled}>
      <div>
        <p>{translate(COOKIE_VIDEO_MESSAGE, locale)}</p>
        <Button href="cookies?redirect=1" hrefType={PAGE_TYPE}>
          {translate(COOKIE_UPDATE, locale)}
        </Button>
      </div>
    </div>
  );
};

export const AutoplayVideo = ({ caption, credits, id, type }) => (
  <Video
    caption={caption}
    credits={credits}
    url={`${getVideoEmbedUrl({ id, type })}${
      type === YOUTUBE
        ? "?rel=0&controls=1&showinfo=0&autoplay=1&loop=1&mute=0"
        : "?title=1&autoplay=1&mute=0"
    }`}
  />
);

function getVideoEmbedUrl({ id, type }) {
  if (type === VIMEO) {
    return `https://player.vimeo.com/video/${id}`;
  }

  if (type === YOUTUBE) {
    return `https://www.youtube-nocookie.com/embed/${id}`;
  }

  if (type === MUX) {
    return `https://stream.mux.com/${id}.m3u8`;
  }
}
